import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { Save, ExpandMore } from '@mui/icons-material';
import { SoWData } from '../services/sowService';

// Quote Item interface - independent from SoW structure
interface QuoteItem {
    phase: string;
    description: string;
    quantifiable_unit: string;
    quantity: number;
    builderRate: number;
    builderTotal: number;
    builderComments: string;
    pricingNote?: string; // Original pricing note from SoW
}

interface BuilderQuoteEditorProps {
    projectId: string;
    sowData: SoWData;
    onSubmitQuote: (items: QuoteItem[], totalPrice: number, additionalNotes?: string) => Promise<void>;
    onDecline: (reason: string) => Promise<void>;
}

const BuilderQuoteEditor: React.FC<BuilderQuoteEditorProps> = ({
    projectId,
    sowData,
    onSubmitQuote,
    onDecline,
}) => {
    const [showDecisionDialog, setShowDecisionDialog] = useState(true);
    const [wantsToQuote, setWantsToQuote] = useState<boolean | null>(null);
    const [declineReason, setDeclineReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [additionalNotes, setAdditionalNotes] = useState('');

    // Initialize quote items from scope of works
    const [quoteItems, setQuoteItems] = useState<QuoteItem[]>(() => {
        const items: QuoteItem[] = [];
        
        sowData.tender_document.scope_of_works.forEach(phase => {
            phase.line_items.forEach(lineItem => {
                items.push({
                    phase: phase.phase_name,
                    description: lineItem.description,
                    quantifiable_unit: lineItem.quantifiable_unit,
                    quantity: lineItem.quantity,
                    builderRate: 0, // Builder fills this in
                    builderTotal: 0,
                    builderComments: '',
                    pricingNote: lineItem.pricing_note
                });
            });
        });
        
        return items;
    });

    const handleRateChange = (index: number, value: string) => {
        const newRate = parseFloat(value) || 0;
        const newItems = [...quoteItems];
        newItems[index].builderRate = newRate;
        newItems[index].builderTotal = newRate * newItems[index].quantity;
        setQuoteItems(newItems);
    };

    const handleCommentChange = (index: number, value: string) => {
        const newItems = [...quoteItems];
        newItems[index].builderComments = value;
        setQuoteItems(newItems);
    };

    const calculateGrandTotal = () => {
        return quoteItems.reduce((sum, item) => sum + (item.builderTotal || 0), 0);
    };

    const calculatePhaseTotal = (phaseName: string) => {
        return quoteItems
            .filter(item => item.phase === phaseName)
            .reduce((sum, item) => sum + (item.builderTotal || 0), 0);
    };

    const handleSubmit = async () => {
        const total = calculateGrandTotal();
        if (total <= 0) {
            alert('Total quote value must be greater than 0');
            return;
        }
        
        // Check if all items have rates
        const missingRates = quoteItems.filter(item => !item.builderRate || item.builderRate <= 0);
        if (missingRates.length > 0) {
            if (!window.confirm(`${missingRates.length} items have no rate set. Continue anyway?`)) {
                return;
            }
        }
        
        setSubmitting(true);
        try {
            await onSubmitQuote(quoteItems, total, additionalNotes);
        } catch (e) {
            console.error(e);
            alert('Failed to submit quote');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDecision = (decision: boolean) => {
        setWantsToQuote(decision);
        if (decision) setShowDecisionDialog(false);
    };

    const handleDeclineSubmit = async () => {
        if (!declineReason.trim()) {
            alert('Please provide a reason');
            return;
        }
        setSubmitting(true);
        try {
            await onDecline(declineReason);
            setShowDecisionDialog(false);
        } catch (e) {
            console.error(e);
            alert('Failed to submit decline');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setShowDecisionDialog(false);
        setWantsToQuote(null);
        setDeclineReason('');
    };

    // Group items by phase
    const phases = Array.from(new Set(quoteItems.map(item => item.phase)));

    if (showDecisionDialog) {
        return (
            <Dialog
                open={true}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Quote for this Project?</DialogTitle>
                <DialogContent>
                    <Typography paragraph>
                        Would you like to provide a quote for <strong>{sowData.tender_document.title}</strong>?
                    </Typography>
                    
                    {wantsToQuote === null && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2" gutterBottom>
                                <strong>Project Duration:</strong> {sowData.internal_estimates.project_duration_weeks_range}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Scope:</strong> {sowData.tender_document.scope_of_works.length} phases with {quoteItems.length} line items
                            </Typography>
                        </Alert>
                    )}
                    
                    {wantsToQuote === false && (
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Reason for declining"
                            value={declineReason}
                            onChange={(e) => setDeclineReason(e.target.value)}
                            sx={{ mt: 2 }}
                            placeholder="e.g., Timeline doesn't work, outside our expertise, too busy..."
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    {wantsToQuote === null ? (
                        <Box display="flex" justifyContent="space-between" width="100%">
                            <Button onClick={handleClose} color="inherit">
                                Decide Later
                            </Button>
                            <Box>
                                <Button onClick={() => handleDecision(false)} color="error">
                                    No, Pass
                                </Button>
                                <Button onClick={() => handleDecision(true)} variant="contained">
                                    Yes, Quote
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            <Button onClick={() => setWantsToQuote(null)}>Back</Button>
                            <Button
                                onClick={handleDeclineSubmit}
                                disabled={!declineReason.trim() || submitting}
                                variant="contained"
                                color="error"
                            >
                                {submitting ? 'Submitting...' : 'Submit Decline'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Box>
            {/* Header Card */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom>
                        {sowData.tender_document.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        {sowData.tender_document.project_summary.description}
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Review all items below and enter your rates. Add comments for any special considerations or alternatives you're proposing.
                    </Alert>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                            <strong>Estimated Duration:</strong> {sowData.internal_estimates.project_duration_weeks_range}
                        </Typography>
                        <Typography variant="h4" color="primary">
                            Total: {calculateGrandTotal().toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            {/* Grouped by Phase */}
            {phases.map((phaseName, phaseIndex) => {
                const phaseItems = quoteItems.filter(item => item.phase === phaseName);
                const phaseTotal = calculatePhaseTotal(phaseName);
                
                return (
                    <Accordion key={phaseIndex} defaultExpanded={phaseIndex === 0}>
                        <AccordionSummary 
                            expandIcon={<ExpandMore />}
                            sx={{ bgcolor: 'grey.100' }}
                        >
                            <Box display="flex" justifyContent="space-between" width="100%" mr={2}>
                                <Typography variant="h6">{phaseName}</Typography>
                                <Typography variant="h6" color="primary">
                                    {phaseTotal.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                                            <TableCell width="35%">Description</TableCell>
                                            <TableCell align="center" width="10%">Qty</TableCell>
                                            <TableCell align="center" width="10%">Unit</TableCell>
                                            <TableCell align="right" width="12%">Rate (£)</TableCell>
                                            <TableCell align="right" width="12%">Total (£)</TableCell>
                                            <TableCell width="21%">Comments</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {phaseItems.map((item, itemIndex) => {
                                            const globalIndex = quoteItems.indexOf(item);
                                            return (
                                                <TableRow key={itemIndex} hover>
                                                    <TableCell>
                                                        <Typography variant="body2">{item.description}</Typography>
                                                        {item.pricingNote && (
                                                            <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.5 }}>
                                                                Note: {item.pricingNote}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <strong>{item.quantity}</strong>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={item.quantifiable_unit} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={item.builderRate || ''}
                                                            onChange={(e) => handleRateChange(globalIndex, e.target.value)}
                                                            inputProps={{ 
                                                                min: 0, 
                                                                step: 0.01,
                                                                style: { textAlign: 'right' } 
                                                            }}
                                                            placeholder="0.00"
                                                            fullWidth
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography 
                                                            variant="body2" 
                                                            fontWeight="bold"
                                                            color={item.builderTotal > 0 ? 'primary' : 'text.disabled'}
                                                        >
                                                            {item.builderTotal.toLocaleString('en-GB', { 
                                                                style: 'currency', 
                                                                currency: 'GBP' 
                                                            })}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            placeholder="Optional notes..."
                                                            value={item.builderComments}
                                                            onChange={(e) => handleCommentChange(globalIndex, e.target.value)}
                                                            multiline
                                                            maxRows={2}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            {/* Additional Notes */}
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Additional Notes & Terms
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Add any additional information, terms, payment schedule, warranty details, or special conditions..."
                        value={additionalNotes}
                        onChange={(e) => setAdditionalNotes(e.target.value)}
                        variant="outlined"
                    />
                </CardContent>
            </Card>

            {/* Submit Button */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" color="text.secondary">
                    Grand Total: <strong style={{ color: '#2e7d32' }}>
                        {calculateGrandTotal().toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                    </strong>
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<Save />}
                    onClick={handleSubmit}
                    disabled={submitting || calculateGrandTotal() <= 0}
                >
                    {submitting ? 'Submitting...' : 'Submit Quote'}
                </Button>
            </Box>
        </Box>
    );
};

export default BuilderQuoteEditor;